import html2canvas from "html2canvas";

const SCALE = 2;
// Chrome caps canvas dimensions around 32,767 px; stay well under it so long
// content renders at a lower scale instead of coming out blank.
const SAFE_CANVAS_LIMIT_PX = 14000;
// ~1.5 lines of text at scale 2, searched upward for a blank row to cut on.
const CLEAN_ROW_WINDOW_PX = 120;

const PDF_OPTIONS = {
  margin: [0.5, 0.4, 0.5, 0.4],
  image: { type: "jpeg", quality: 0.98 },
  jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
};

const isScrollable = (value) => value === "auto" || value === "scroll";

// Runs on html2canvas's cloned document only, so the live modal is untouched.
// Scroll boxes inside the element (chat history, table) are expanded to their
// full content, and the fixed/scrolling modal ancestors stop clipping it.
const expandClippedContent = (clonedDoc, clonedElement) => {
  const view = clonedDoc.defaultView;

  Array.from(clonedElement.querySelectorAll("*")).forEach((node) => {
    const style = view.getComputedStyle(node);
    if (isScrollable(style.overflowY)) {
      node.style.maxHeight = "none";
      node.style.overflow = "visible";
    }
  });

  for (let node = clonedElement.parentElement; node; node = node.parentElement) {
    if (view.getComputedStyle(node).position === "fixed") {
      node.style.position = "absolute";
    }
    node.style.height = "auto";
    node.style.maxHeight = "none";
    node.style.overflow = "visible";
  }
};

// Picks the mostly-blank row nearest above targetY so a page break never
// slices through a line of text.
const findCleanCutY = (canvas, targetY) => {
  const ctx = canvas.getContext("2d");
  let bestY = targetY;
  let minDark = canvas.width + 1;
  const searchLimit = Math.max(0, targetY - CLEAN_ROW_WINDOW_PX);
  for (let y = targetY; y >= searchLimit; y--) {
    const { data } = ctx.getImageData(0, y, canvas.width, 1);
    let dark = 0;
    for (let p = 0; p < data.length; p += 4) {
      if (data[p] < 240 || data[p + 1] < 240 || data[p + 2] < 240) dark++;
    }
    if (dark < minDark) {
      minDark = dark;
      bestY = y;
      if (dark === 0) break;
    }
  }
  return bestY;
};

export const toPdfFileName = (name) => {
  const cleaned = String(name || "")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  return `${cleaned || "Download"}.pdf`;
};

// Captures the element in place (so styles scoped to its ancestors, e.g.
// .bwai-response-modal, still apply) and saves it as a paginated PDF. Nodes
// marked with `data-html2canvas-ignore`, and Bootstrap's modal close (×)
// button, are left out of the capture.
export const downloadElementAsPdf = async (element, fileName) => {
  const scale = Math.min(
    SCALE,
    SAFE_CANVAS_LIMIT_PX / Math.max(element.scrollHeight, 1)
  );

  const canvas = await html2canvas(element, {
    scale,
    useCORS: true,
    scrollX: 0,
    scrollY: 0,
    ignoreElements: (node) =>
      Boolean(node.classList && node.classList.contains("close")),
    onclone: expandClippedContent,
  });

  // Same bootstrap as TriviaResultsSummary: get a correctly sized jsPDF from
  // html2pdf via a 1px placeholder, then drop its empty seed page.
  const seedDiv = document.createElement("div");
  seedDiv.style.cssText =
    "position:absolute;left:-9999px;top:0;width:1px;height:1px;";
  document.body.appendChild(seedDiv);
  let pdf;
  try {
    pdf = await window
      .html2pdf()
      .from(seedDiv)
      .set(PDF_OPTIONS)
      .toPdf()
      .get("pdf");
  } finally {
    document.body.removeChild(seedDiv);
  }
  pdf.deletePage(1);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const [marginTop, marginRight, marginBottom, marginLeft] = PDF_OPTIONS.margin;
  const contentWidth = pageWidth - marginLeft - marginRight;
  const contentHeight = pageHeight - marginTop - marginBottom;
  const pageContentPx = Math.floor((canvas.width * contentHeight) / contentWidth);

  let yPx = 0;
  while (yPx < canvas.height) {
    const rawCutY = yPx + pageContentPx;
    let cutY = canvas.height;
    if (rawCutY < canvas.height) {
      cutY = findCleanCutY(canvas, rawCutY);
      // A fully inked window has no blank row; fall back to a hard cut.
      if (cutY <= yPx) cutY = rawCutY;
    }
    const sliceHeightPx = cutY - yPx;

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = sliceHeightPx;
    const sliceCtx = sliceCanvas.getContext("2d");
    sliceCtx.fillStyle = "#ffffff";
    sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
    sliceCtx.drawImage(
      canvas,
      0,
      yPx,
      canvas.width,
      sliceHeightPx,
      0,
      0,
      canvas.width,
      sliceHeightPx
    );

    const sliceData = sliceCanvas.toDataURL(
      `image/${PDF_OPTIONS.image.type}`,
      PDF_OPTIONS.image.quality
    );
    const sliceHeightInches = (sliceHeightPx * contentWidth) / canvas.width;

    pdf.addPage();
    pdf.addImage(
      sliceData,
      "JPEG",
      marginLeft,
      marginTop,
      contentWidth,
      sliceHeightInches
    );

    yPx = cutY;
  }

  pdf.save(fileName);
};
